import { MouseEventHandler, useCallback, useEffect, useState } from 'react';
import SignupLink, {
  type SignupLinkParams,
} from '@bridebook/web/app-shared/components/signup-link/signup-link';
import { cn, IconButton } from '@bridebook/aura';
import { HeartOutline, HeartSolid } from '@bridebook/aura/src/icons';

export interface SaveToShortlistButtonProps
  extends Pick<SignupLinkParams, 'cta' | 'clickedSection' | 'clickedLocation'> {
  isLoggedIn: boolean;
  supplierId: string;
  toggle(isShortlisted: boolean): void;
  variantSize?: 'sm' | 'standard';
  isShortlisted: boolean;
  onShortlist?: () => void;
  className?: string;
  classNames?: { icon?: string; loadingIcon?: string };
}

const SaveToShortlistButton = ({
  toggle,
  onShortlist,
  variantSize = 'standard',
  isShortlisted,
  isLoggedIn,
  cta = 'shortlistButton',
  clickedSection = 'supplierMediaGallery',
  clickedLocation = 'supplierProfile',
  className,
  classNames,
}: SaveToShortlistButtonProps) => {
  const [animationKey, setAnimationKey] = useState(0);

  const toggleShortlisted: MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      if (isShortlisted) {
        setAnimationKey((k) => k + 1);
      }
      toggle(isShortlisted);
    },
    [toggle, isShortlisted],
  );

  useEffect(() => {
    if (onShortlist && isShortlisted) {
      onShortlist();
    }
  }, [isShortlisted, onShortlist]);

  if (isLoggedIn) {
    return (
      <IconButton
        key={animationKey}
        icon={isShortlisted ? HeartSolid : HeartOutline}
        variant="outline"
        size={variantSize}
        className={className}
        classNames={{
          icon: cn('text-blush-tangerine-120', isShortlisted && 'heart-beat', classNames?.icon),
          loadingIcon: classNames?.loadingIcon,
        }}
        data-test="save-to-shortlist"
        onClick={toggleShortlisted}
      />
    );
  }

  return (
    <SignupLink clickedLocation={clickedLocation} clickedSection={clickedSection} cta={cta}>
      <IconButton
        icon={isShortlisted ? HeartSolid : HeartOutline}
        variant="outline"
        size={variantSize}
        className={className}
        classNames={{
          icon: cn('text-blush-tangerine-120', classNames?.icon),
          loadingIcon: classNames?.loadingIcon,
        }}
        data-test="save-to-shortlist"
      />
    </SignupLink>
  );
};

export default SaveToShortlistButton;
